import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
    catchError,
    combineLatest,
    concatMap,
    filter,
    first,
    from,
    interval,
    map,
    of,
    switchMap,
    take,
    takeUntil,
    timer,
    withLatestFrom,
} from 'rxjs';

import { PrivaLocalizationService } from '@priva/localization';
import { PrivaNotificationsService } from '@priva/notifications';

import { Registration, RegistrationType } from 'app/features/manual-input-registration/models';
import {
    selectAppMode,
    selectAppModeIfDefined,
} from 'app/features/settings/state/settings/settings.selectors';
import { getApplicationName } from 'app/features/settings/utils';
import {
    loginRegistrationDefinition,
    logoutRegistrationDefinition,
    startBreakRegistrationDefinition,
} from 'app/shared/components/system-and-break-activity/models';
import { notificationSuccessTimeout } from 'app/shared/constants';
import { ActivityType } from 'app/shared/features/activity-selector/models';
import { selectBatchNameByNumber } from 'app/shared/features/batch-selector/state/batch.selectors';
import { createLocationRegistrationFromLocationSelection } from 'app/shared/features/location-selector/models';
import {
    createRowRegistrationFromRowSelection,
    RowRegistration,
} from 'app/shared/features/map-row-selector/models';
import {
    DeviceInfoStorageService,
    registrationDataStorageKey,
    StorageService,
    TerminalClient,
} from 'app/shared/services';
import { toHoursAndMinutes } from 'app/shared/utils';
import { selectActivityByNumber } from 'app/state/activities/activity.selectors';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';
import { selectApiConfigState } from 'app/state/api-config/api-config.selectors';
import { selectContainerListbyNumbers } from 'app/state/containers/container.selectors';
import { selectEmployeeByNumber } from 'app/state/employee/employee.selectors';
import { selectRowListByNumbers } from 'app/state/green-house-map/green-house-map.selectors';
import { selectLocationNameByNumber } from 'app/state/locations/locations.selectors';
import { selectProductBoxListBuNumbers } from 'app/state/product-boxes/product-box.selectors';
import { getSelectedRows } from 'app/state/row-selection/row-selection.selectors';
import {
    selectUserSessionCredentials,
    selectUserSessionEmployeeNumber,
} from 'app/state/user-session/user-session.selectors';

import {
    addPackingRegistration,
    addRegistrationForCurrentRowSelection,
    addRegistrationsToCache,
    addTimeRegistration,
    initializeRegistrationStore,
    monitorRowRegistrationsForActivity,
    resetRegistrationState,
    rewriteServerRowRegistrationsForActivity,
    sendCachedRegistrations,
    sendCachedRegistrationsFailure,
    sendCachedRegistrationsSuccess,
    showingToastAfterRegistration,
    showingToastAfterRegistrationSuccess,
    stopMonitorRowRegistrationsForActivity,
} from './registrations.actions';
import { selectCachedRegistrationsAsArray, selectRegistrationsState } from './registrations.selectors';
import { AppMode, getLabourRegistrationMessage } from './registrations.utils';

const retrySendRegistrationsIntervalMs = 60000;
const fetchServerRowRegistrationsIntervalMs = 60000;

// noinspection JSUnusedGlobalSymbols
@Injectable()
export class RegistrationsEffects {
    privaLocalizationService = inject(PrivaLocalizationService);

    loadFromStorage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeRegistrationStore),
            map(
                () =>
                    this.storageService.get<Partial<Registration>[]>(registrationDataStorageKey)?.map(
                        (p): Registration => ({
                            activityNumber: p.activityNumber || 0,
                            amountResult: p.amountResult || undefined,
                            batchNumber: p.batchNumber || undefined,
                            containerNumbers: p.containerNumbers || [],
                            employeeNumber: p.employeeNumber || 0,
                            productBoxRegistrations: p.productBoxRegistrations || [],
                            registrationTimestamp: p.registrationTimestamp || 0,
                            registrationType: p.registrationType ?? RegistrationType.Labour,
                            rowRegistrations: p.rowRegistrations || [],
                            locationRegistrations: p.locationRegistrations || undefined,
                        }),
                    ) ?? [],
            ),
            filter((registrations) => !!registrations.length),
            map((registrations) => addRegistrationsToCache({ registrations })),
        ),
    );

    periodicallySendCachedRegistrations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initializeRegistrationStore),
            switchMap(() => interval(retrySendRegistrationsIntervalMs)),
            map(() => sendCachedRegistrations()),
        ),
    );
    // registration with type login, logout, start end break
    addTimeRegistration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addTimeRegistration),
            map(({ activityNumber, registrationTimestamp, registrationType, employeeNumber }) => {
                const registration: Registration = {
                    amountResult: undefined,
                    activityNumber: activityNumber ?? 0,
                    batchNumber: undefined,
                    containerNumbers: [],
                    employeeNumber,
                    productBoxRegistrations: [],
                    registrationTimestamp,
                    registrationType,
                    rowRegistrations: [],
                    locationRegistrations: undefined,
                };
                return registration;
            }),
            withLatestFrom(this.store.select(selectAppMode)),
            switchMap(([registration, appMode]) => {
                // If there's login or logout registration, and app mode is personal, ignore toast message
                // Otherwise, display toast message as multiuser mode may contain multiple users login or logout
                if (
                    (registration.registrationType === RegistrationType.Login ||
                        registration.registrationType === RegistrationType.Logout ||
                        registration.registrationType === RegistrationType.StartPause) &&
                    appMode === AppMode.Personal
                ) {
                    return [addRegistrationsToCache({ registrations: [registration] })];
                }
                return [
                    addRegistrationsToCache({ registrations: [registration] }),
                    showingToastAfterRegistration({ registrations: [registration] }),
                ];
            }),
        );
    });

    addRegistrationForCurrentRowSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addRegistrationForCurrentRowSelection),
            withLatestFrom(
                this.store.select(getSelectedRows),
                this.store.select(selectAppMode),
                this.store.select(selectUserSessionEmployeeNumber),
            ),
            map(
                ([
                    {
                        activityNumber,
                        amountResult,
                        batchNumber,
                        containers,
                        employeeNumberMultiUserMode,
                        productBoxRegistrations,
                        registrationType,
                        location,
                    },
                    rowSelection,
                    appMode,
                    userSessionEmployeeNumber,
                ]): Registration | undefined => {
                    const employeeNumberForRegistration =
                        appMode === AppMode.MultiUser
                            ? employeeNumberMultiUserMode
                            : userSessionEmployeeNumber;

                    if (!employeeNumberForRegistration) {
                        return undefined;
                    }
                    const locationRegistrations = location
                        ? createLocationRegistrationFromLocationSelection(location)
                        : undefined;
                    const rowRegistrations = rowSelection.map(createRowRegistrationFromRowSelection);

                    return {
                        activityNumber,
                        amountResult,
                        batchNumber,
                        containerNumbers: containers.map((c) => c.number) || [],
                        employeeNumber: employeeNumberForRegistration,
                        productBoxRegistrations,
                        registrationTimestamp: Date.now(),
                        registrationType,
                        rowRegistrations,
                        locationRegistrations,
                    };
                },
            ),
            filter((registration): registration is Registration => !!registration),
            switchMap((registration) => {
                return [
                    addRegistrationsToCache({ registrations: [registration] }),
                    showingToastAfterRegistration({ registrations: [registration] }),
                ];
            }),
        );
    });

    //packing registration
    addPackingRegistration$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addPackingRegistration),
            withLatestFrom(
                this.store.select(getSelectedRows),
                this.store.select(selectAppMode),
                this.store.select(selectUserSessionEmployeeNumber),
            ),
            map(
                ([
                    { activityNumber, employeeNumberMultiUserMode, batchNumber, location },
                    rowSelection,
                    appMode,
                    userSessionEmployeeNumber,
                ]): Registration | undefined => {
                    const employeeNumberForRegistration =
                        appMode === AppMode.MultiUser
                            ? employeeNumberMultiUserMode
                            : userSessionEmployeeNumber;

                    if (!employeeNumberForRegistration) {
                        return undefined;
                    }
                    const locationRegistrations = location
                        ? createLocationRegistrationFromLocationSelection(location)
                        : undefined;

                    const rowRegistrations = rowSelection.map(createRowRegistrationFromRowSelection);

                    return {
                        activityNumber,
                        amountResult: undefined,
                        batchNumber,
                        containerNumbers: [],
                        employeeNumber: employeeNumberForRegistration,
                        productBoxRegistrations: [],
                        registrationTimestamp: Date.now(),
                        registrationType: RegistrationType.Labour,
                        rowRegistrations: rowRegistrations,
                        locationRegistrations,
                    };
                },
            ),
            filter((registration): registration is Registration => !!registration),
            switchMap((registration) => {
                return [
                    addRegistrationsToCache({ registrations: [registration] }),
                    showingToastAfterRegistration({ registrations: [registration] }),
                ];
            }),
        );
    });

    showToastAfterRegistration = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(showingToastAfterRegistration),
                switchMap(({ registrations }) => {
                    return from(registrations);
                }),
                switchMap((registration) => {
                    return combineLatest([
                        this.store.pipe(selectEmployeeByNumber(registration.employeeNumber)),
                        this.store.pipe(selectActivityByNumber(registration.activityNumber)),
                        this.store.select(
                            selectProductBoxListBuNumbers(
                                registration.productBoxRegistrations.map(
                                    ({ productBoxNumber }) => productBoxNumber,
                                ),
                            ),
                        ),
                        this.store.select(
                            selectRowListByNumbers(
                                registration.rowRegistrations.map(({ rowNumber }) => rowNumber),
                            ),
                        ),
                        this.store.select(selectContainerListbyNumbers(registration.containerNumbers)),
                        this.store.select(
                            selectLocationNameByNumber(registration.locationRegistrations?.locationNumber),
                        ),
                        this.store.select(selectBatchNameByNumber(registration.batchNumber)),
                    ]).pipe(
                        first(),
                        map(([employee, activity, productBox, row, container, locationName, batchName]) => ({
                            registration,
                            employee,
                            activity,
                            productBox,
                            row,
                            container,
                            locationName,
                            batchName,
                        })),
                    );
                }),
                map(
                    ({
                        registration,
                        employee,
                        activity,
                        productBox,
                        row,
                        container,
                        locationName,
                        batchName,
                    }) => {
                        let toasterMessage = '';
                        switch (registration.registrationType) {
                            case RegistrationType.Labour:
                                toasterMessage = getLabourRegistrationMessage(
                                    registration,
                                    activity,
                                    productBox,
                                    row,
                                    container,
                                    locationName,
                                    batchName,
                                );
                                break;
                            case RegistrationType.StartPause:
                                toasterMessage = startBreakRegistrationDefinition.name;
                                break;
                            case RegistrationType.EndPause:
                                toasterMessage = 'GENERAL.TITLE.END_BREAK_ACTIVITY';
                                break;
                            case RegistrationType.Login:
                                toasterMessage = loginRegistrationDefinition.name;
                                break;
                            case RegistrationType.Logout:
                                toasterMessage = logoutRegistrationDefinition.name;
                                break;
                        }
                        this.notificationsService.toaster.success(
                            {
                                emphasized: `${employee?.name}`,
                                message: toasterMessage,
                                emphasisBlock: true,
                                params: { activityName: activity?.name },
                            },
                            { behavior: 'transient', timeout: notificationSuccessTimeout },
                        );
                        return;
                    },
                ),
            );
        },
        { dispatch: false },
    );

    sendCachedRegistrations$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sendCachedRegistrations),
            concatMap(() => {
                return combineLatest([
                    this.store.select(selectRegistrationsState),
                    this.store.select(selectUserSessionCredentials),
                    this.store.select(selectApiConfigState),
                    this.store.pipe(selectAppModeIfDefined),
                ]).pipe(
                    take(1),
                    filter(([, , { identifier }]) => !!identifier),
                    switchMap(
                        ([
                            { cachedRegistrations, previousUserSessionCredentials },
                            userSessionCredentials,
                            { identifier, apiVersion },
                            appMode,
                        ]) => {
                            // When cached registrations exceed a certain limit, it may not be sent to the server to large amount limit
                            // To prevent this from happening, registrations will be sent in batches of 200 at one time
                            const sendCachedRegistrations = Object.fromEntries(
                                Object.entries(cachedRegistrations).slice(0, 200),
                            );
                            const registrations = Object.values(sendCachedRegistrations);
                            if (!registrations.length)
                                return of(sendCachedRegistrationsSuccess({ sentRegistrations: {} }));

                            const applicationName = getApplicationName(appMode);
                            const deviceId = this.deviceInfoStorageService.getOrAddDeviceId();

                            const credentials = userSessionCredentials ?? previousUserSessionCredentials;

                            if (!credentials) {
                                return of(
                                    sendCachedRegistrationsFailure({
                                        errorMessage:
                                            this.privaLocalizationService.instant('ERROR.TITLE.UNAUTHORIZED'),
                                    }),
                                );
                            }
                            return this.client
                                .sendRegistrations({
                                    registrations,
                                    credentials,
                                    apiVersion,
                                    identifier,
                                    deviceId,
                                    applicationName,
                                    containerMeasurements: [],
                                })
                                .pipe(
                                    switchMap((result) => {
                                        if (!result.success)
                                            return [
                                                sendCachedRegistrationsFailure({
                                                    errorMessage: result.error,
                                                }),
                                            ];
                                        return result.feedback
                                            ? [
                                                  sendCachedRegistrationsSuccess({
                                                      sentRegistrations: sendCachedRegistrations,
                                                  }),
                                                  showingToastAfterRegistrationSuccess({
                                                      feedback: result.feedback,
                                                  }),
                                              ]
                                            : [
                                                  sendCachedRegistrationsSuccess({
                                                      sentRegistrations: sendCachedRegistrations,
                                                  }),
                                              ];
                                    }),
                                    catchError((e) =>
                                        of(sendCachedRegistrationsFailure({ errorMessage: e.message })),
                                    ),
                                );
                        },
                    ),
                );
            }),
        ),
    );

    showToastAfterRegistrationSuccess$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(showingToastAfterRegistrationSuccess),
                map(({ feedback }) => feedback),
                switchMap((feedback) => {
                    return combineLatest([
                        this.store.pipe(selectActivityByNumber(feedback.activityNumber)),
                    ]).pipe(
                        first(),
                        map(([activity]) => ({
                            feedback,
                            activity,
                        })),
                    );
                }),
                map(({ feedback, activity }) => {
                    // Display toaster message only when activity is a type of labour registration
                    // Activity should not contain type break or number 0 (login / logout)
                    if (
                        activity?.activityType === ActivityType.Break ||
                        feedback.activityNumber === 0 ||
                        !feedback.workedHours
                    )
                        return;

                    let toasterMessage;

                    // Display only worked hours when there's no presentationUnit
                    // If there's no presentationUnit, there should not be any performance data returned
                    // Otherwise, there will be a configuration issue which should be corrected in FS Portal
                    // Sometimes there's no performance data but only hoursWorked, therefore only display hoursWorked text
                    if (
                        !activity?.presentationUnit ||
                        (!feedback.performance && !feedback.averagePerformance)
                    ) {
                        toasterMessage = this.privaLocalizationService.instant(
                            'TOASTER.TEXT.WORKED_HOURS_ONLY',
                            {
                                workedHours: toHoursAndMinutes(feedback.workedHours),
                            },
                        );
                    } else {
                        const performanceDataWithUnit = `${feedback.performance!.toFixed(2)} ${activity?.presentationUnit?.toString().toLowerCase()}`;
                        const averagePerformanceDataWithUnit = `${feedback.averagePerformance!.toFixed(2)} ${activity?.presentationUnit?.toString().toLowerCase()}`;
                        const hoursWorkedText = this.privaLocalizationService.instant(
                            'TOASTER.TEXT.WORKED_HOURS_PERFORMANCE',
                            {
                                workedHours: toHoursAndMinutes(feedback.workedHours),
                                performance: performanceDataWithUnit,
                            },
                        );
                        const averagePerformanceText = this.privaLocalizationService.instant(
                            'TOASTER.TEXT.AVERAGE_PERFORMANCE',
                            {
                                averagePerformance: averagePerformanceDataWithUnit,
                            },
                        );
                        toasterMessage = hoursWorkedText + '<br/>' + averagePerformanceText;
                    }

                    this.notificationsService.toaster.success(
                        {
                            emphasized: `${activity?.name}`,
                            message: toasterMessage,
                            emphasisBlock: true,
                        },
                        { behavior: 'transient', timeout: notificationSuccessTimeout },
                    );
                }),
            );
        },
        { dispatch: false },
    );

    sendRegistrationsOnCacheChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addRegistrationsToCache),
            map(() => sendCachedRegistrations()),
        );
    });

    storeRegistrations$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(addRegistrationsToCache, sendCachedRegistrationsSuccess),
                withLatestFrom(this.store.select(selectCachedRegistrationsAsArray)),
                map(([, registrations]) => {
                    this.storageService.store(registrationDataStorageKey, registrations);
                    return true;
                }),
            );
        },
        { dispatch: false },
    );

    clearRegistrationsInStorage$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(resetRegistrationState),
                map(() => {
                    this.storageService.clear(registrationDataStorageKey);
                    return true;
                }),
            ),
        { dispatch: false },
    );

    resetRegistrationStateAfterResetApiConfig$ = createEffect(() =>
        this.actions$.pipe(
            ofType(resetApiConfig),
            map(() => resetRegistrationState()),
        ),
    );

    monitorRegistrationsForActivity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(monitorRowRegistrationsForActivity),
            withLatestFrom(this.store.select(selectApiConfigState)),
            switchMap(([{ activityNumber }, { identifier, apiVersion }]) =>
                timer(0, fetchServerRowRegistrationsIntervalMs).pipe(
                    takeUntil(
                        this.actions$.pipe(
                            ofType(stopMonitorRowRegistrationsForActivity, resetRegistrationState),
                        ),
                    ),
                    map(() => ({ activityNumber, identifier, apiVersion })),
                ),
            ),
            switchMap(({ activityNumber, identifier, apiVersion }) => {
                return this.client
                    .getRowRegistrationsForActivity({
                        activityNumber,
                        identifier,
                        apiVersion,
                    })
                    .pipe(
                        map((response) => ({
                            rowRegistrations: response?.rowRegistrations ?? [],
                            activityNumber,
                        })),
                    );
            }),
            map(({ rowRegistrations, activityNumber }) => {
                const rowRegistrationsMapped: RowRegistration[] = rowRegistrations.map(
                    ({ locationNumber, locationFinished }) => ({
                        rowNumber: locationNumber,
                        registrationStatus: locationFinished ? 'done' : 'incomplete',
                    }),
                );
                return rewriteServerRowRegistrationsForActivity({
                    activityNumber,
                    rowRegistrations: rowRegistrationsMapped,
                });
            }),
        ),
    );

    constructor(
        private actions$: Actions,
        private store: Store,
        private storageService: StorageService,
        private client: TerminalClient,
        private deviceInfoStorageService: DeviceInfoStorageService,
        private notificationsService: PrivaNotificationsService,
    ) {}
}
